import { useCallback, useEffect, useState } from 'react';
import { collection, doc, getDoc } from 'firebase/firestore';
import { useAuth } from './useAuth';
import {
  classesService,
  paymentsService,
  studentsService,
} from '../services/firestoreService';
import teacherActionsService from '../services/teacherActionsService';
import assignmentSubmissionService from '../services/assignmentSubmissionService';
import { db } from '../services/firebase';
import { Class, Payment } from '../types/firestore';

interface StudentStats {
  enrolledClasses: number;
  completedSessions: number;
  upcomingAssignments: number;
  averageGrade: number;
  totalPayments: number;
  outstandingPayments: number;
}

interface StudentClass {
  id: string;
  name: string;
  subject: string;
  teacherName: string;
  schedule: string;
  nextSession?: Date;
}

const getTeacherName = async (teacherId: string): Promise<string> => {
  if (!teacherId) return 'غير محدد';
  const teacherSnapshot = await getDoc(doc(collection(db, 'users'), teacherId));
  if (!teacherSnapshot.exists()) return 'غير محدد';
  const teacherData = teacherSnapshot.data();
  return teacherData.name || teacherData.displayName || teacherData.email || 'غير محدد';
};

export const useStudentData = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const userRole = user?.role;
  const [stats, setStats] = useState<StudentStats | null>(null);
  const [classes, setClasses] = useState<StudentClass[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStudentData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const studentData = await studentsService.getById(userId);

      const [validClasses, studentPayments, studentAssignments, submissions] =
        await Promise.all([
          classesService.getByStudentId(userId),
          paymentsService.getByStudentId(userId),
          teacherActionsService.getStudentAssignments(userId),
          assignmentSubmissionService.getStudentSubmissions(userId),
        ]);

      const teacherIds = Array.from(
        new Set(
          [
            ...validClasses.map((cls: Class) => cls.teacherId),
            ...studentAssignments.map(assignment => assignment.teacherId),
          ].filter(Boolean)
        )
      );
      const teacherEntries = await Promise.all(
        teacherIds.map(async teacherId => [teacherId, await getTeacherName(teacherId)] as const)
      );
      const teachersMap = new Map<string, string>(teacherEntries);

      const studentClasses: StudentClass[] = validClasses.map(cls => ({
        id: cls.id!,
        name: cls.name,
        subject: cls.subject,
        teacherName: teachersMap.get(cls.teacherId) || 'غير محدد',
        schedule: cls.schedule
          ? `${cls.schedule.dayOfWeek} - ${cls.schedule.startTime}`
          : 'غير محدد',
      }));

      const submissionsByAssignment = new Map(
        submissions.map(submission => [
          `${submission.teacherId}:${submission.assignmentId}`,
          submission,
        ])
      );
      const assignmentsWithSubmissions = studentAssignments.map(assignment => {
        const submission = submissionsByAssignment.get(
          `${assignment.teacherId}:${assignment.id}`
        );
        const dueDate = assignment.dueDate
          ? new Date(assignment.dueDate)
          : null;
        const isOverdue =
          !submission &&
          dueDate &&
          !Number.isNaN(dueDate.getTime()) &&
          dueDate.getTime() < Date.now();

        return {
          ...assignment,
          teacherName: teachersMap.get(assignment.teacherId) || 'غير محدد',
          status: submission?.status || (isOverdue ? 'overdue' : 'active'),
          submission,
          submissionId: submission?.id,
          grade: submission?.grade,
          feedback: submission?.feedback,
        };
      });

      const pendingAssignments = assignmentsWithSubmissions.filter(assignment =>
        ['pending', 'active', 'overdue'].includes(assignment.status || 'active')
      ).length;

      setStats({
        enrolledClasses: studentClasses.length,
        completedSessions: studentData?.totalClassesAttended || 0,
        upcomingAssignments: pendingAssignments,
        averageGrade: studentData?.averageGrade || 0,
        totalPayments: studentPayments.filter(p => p.status === 'paid').length,
        outstandingPayments: studentPayments.filter(p =>
          ['pending', 'overdue'].includes(p.status)
        ).length,
      });
      setClasses(studentClasses);
      setPayments(studentPayments);
      setAssignments(assignmentsWithSubmissions);
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError('حدث خطأ في جلب بيانات الطالب');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userRole === 'student') {
      fetchStudentData();
    }
  }, [fetchStudentData, userRole]);

  return {
    stats,
    classes,
    assignments,
    payments,
    loading,
    error,
    refreshData: fetchStudentData,
  };
};

export default useStudentData;
